import React, { useMemo } from "react";
import { CCField, CCSelect, CCSpinner } from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCircleCheck,
	faCirclePlus,
	faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { EditorGroup, useEditorGroups } from "../../hooks/useMyEditorGroups";
import { EventDoc, EventGroup } from "../../types/event";
import { app } from "../../config";
import "./eventManager.css";

/**
 * A chosen group's name. `Label` is the event document's field; `label` is what
 * this app wrote before that was noticed, and events carry both.
 */
function groupLabel(group: EventGroup): string {
	return group.Label || group.label || group._id;
}

interface EventGroupsTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * Groups tab, following pymble-swipe's GroupsTab: a category select and a
 * search across the top, the matches listed on the left with an add control per
 * row, and what has been chosen in a panel on the right.
 *
 * The search is a typeahead (see useEditorGroups) rather than type-then-press:
 * the design has no Search button, and a group picker where nothing happens
 * until you press one reads as broken.
 *
 * NOT carried over from the design: the "Allow Signup" toggle and the
 * Attendees/Viewers pills beside each chosen group. The S2 event document has
 * no per-group signup or visibility field to write them to — `SelectedGroups`
 * holds an id, a label, an icon and a member count and nothing else — so the
 * control would be a switch wired to nothing, which is worse than its absence.
 * Same reason the Requirements and Event Owner concepts were left out; see the
 * table in CLAUDE.md.
 */
export const EventGroupsTab: React.FC<EventGroupsTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const {
		groupTypes,
		term,
		setTerm,
		groupType,
		setGroupType,
		longEnough,
		minQueryLength,
		groups,
		searching,
		searchFailed,
	} = useEditorGroups();

	/**
	 * Chosen groups, as ids and in order. Entries are id strings on any event v2
	 * saved, and objects on one this app saved before that was noticed.
	 */
	const selectedIds: string[] = useMemo(() => {
		const entries = Array.isArray(event.SelectedGroups)
			? event.SelectedGroups
			: [];
		return entries
			.map((e) => (typeof e === "string" ? e : e?._id))
			.filter((id): id is string => !!id);
	}, [event.SelectedGroups]);

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	/**
	 * Id → the group's details, gathered from every source that has them.
	 *
	 * `Groups` first because that is the API's own expansion and the only source
	 * that survives a reload; then any object sitting in `SelectedGroups`; then
	 * the current search, which is what names a group in the moment it is added,
	 * before anything has been saved.
	 */
	const detailsById = useMemo(() => {
		const map = new Map<string, EventGroup>();
		for (const g of event.Groups ?? []) {
			if (g?._id) map.set(g._id, g);
		}
		for (const e of event.SelectedGroups ?? []) {
			if (typeof e !== "string" && e?._id) {
				map.set(e._id, { ...map.get(e._id), ...e });
			}
		}
		for (const g of groups) {
			if (!map.has(g.value)) {
				map.set(g.value, {
					_id: g.value,
					Label: g.label,
					Icon: g.Icon,
					MemberCount: g.MemberCount,
				});
			}
		}
		return map;
	}, [event.Groups, event.SelectedGroups, groups]);

	// CCSelect takes an options array rather than <option> children, so the
	// dictionary is mapped once per change instead of on every render.
	const groupTypeOptions = useMemo(
		() =>
			groupTypes.map((t: { id: string; value: string }) => ({
				value: t.id,
				label: t.value,
			})),
		[groupTypes]
	);

	/** The design names the current filter back to the person. "All" when none. */
	const groupTypeLabel = useMemo(
		() =>
			groupTypeOptions.find((o: { value: string }) => o.value === groupType)
				?.label ?? "All",
		[groupTypeOptions, groupType]
	);

	/** v2's stored shape, field for field, for the expanded `Groups` list. */
	const asEventGroup = (g: EditorGroup): EventGroup => ({
		_id: g.value,
		Label: g.label,
		Icon: g.Icon,
		MemberCount: g.MemberCount,
		Members: [],
	});

	/**
	 * Both halves move together: ids into `SelectedGroups`, which is what the
	 * API stores and what every consumer of the audience reads, and the matching
	 * document into `Groups`, so the row has a name before the event has been
	 * saved and re-expanded — including after a trip to another tab, which
	 * unmounts this one and would drop anything held in local state.
	 */
	const addGroups = (added: EditorGroup[]) => {
		const fresh = added.filter((g) => !selectedSet.has(g.value));
		if (fresh.length === 0) return;
		onChange({
			SelectedGroups: [...selectedIds, ...fresh.map((g) => g.value)],
			Groups: [...(event.Groups ?? []), ...fresh.map(asEventGroup)],
		});
	};

	const removeGroup = (id: string) =>
		onChange({
			SelectedGroups: selectedIds.filter((g) => g !== id),
			Groups: (event.Groups ?? []).filter((g) => g._id !== id),
		});

	/** Everything currently listed, minus what is already chosen. */
	const unselectedMatches = useMemo(
		() => groups.filter((g) => !selectedSet.has(g.value)),
		[groups, selectedSet]
	);

	/** What the left list says when it has no rows, which is four situations. */
	const emptyMessage = searchFailed
		? "Could not run that search. Please try again."
		: !longEnough
			? `Type at least ${minQueryLength} characters to find a group.`
			: searching
				? "Searching…"
				: "No groups matched that search.";

	return (
		<div>
			<h6 className="em-section-heading">Groups</h6>
			<p className="em-section-note">
				Groups added here will be able to see the event in their calendar
			</p>

			<div className="em-group-filters">
				<CCSelect
					label="Group category"
					options={groupTypeOptions}
					placeholder="All"
					fullWidth
					value={groupType}
					disabled={readOnly}
					onChange={(e) => setGroupType(e.target.value)}
				/>
				<CCField
					label="Search Group"
					type="search"
					fullWidth
					value={term}
					disabled={readOnly}
					onChange={(e) => setTerm(e.target.value)}
				/>
			</div>

			<div className="em-picker-columns">
				<div>
					<div className="em-group-listhead">
						<span className="em-muted">
							Group Type : <strong>{groupTypeLabel}</strong>
						</span>
						{/* Selects everything the search currently lists, not every
						    group in the school — the same scope the design's button
						    has, and the only one that is safe: this writes straight
						    into the event's audience. */}
						<button
							type="button"
							className="em-group-selectall"
							disabled={readOnly || unselectedMatches.length === 0}
							onClick={() => addGroups(unselectedMatches)}
						>
							<FontAwesomeIcon icon={faCirclePlus} />
							Select All
						</button>
					</div>

					<div className="em-group-list">
						{searching && groups.length === 0 ? (
							<div className="em-picker-empty">
								<CCSpinner size="inline" label="Searching..." />
							</div>
						) : groups.length === 0 ? (
							<div className="em-picker-empty">{emptyMessage}</div>
						) : (
							groups.map((g) => {
								const already = selectedSet.has(g.value);
								return (
									<div className="em-group-row" key={g.value}>
										<span className="em-group-row__icon">
											<img
												src={g.Icon || app.defaultGroupIcon}
												alt=""
											/>
										</span>
										<span className="em-group-row__main">
											<strong>{g.label}</strong>
											<small>
												{g.MemberCount} Member
												{g.MemberCount === 1 ? "" : "s"}
											</small>
										</span>
										<button
											type="button"
											className={
												already
													? "em-group-row__action is-on"
													: "em-group-row__action"
											}
											disabled={readOnly || already}
											title={already ? "Already added" : "Add group"}
											aria-label={
												already
													? `${g.label} already added`
													: `Add ${g.label}`
											}
											onClick={() => addGroups([g])}
										>
											<FontAwesomeIcon
												icon={already ? faCircleCheck : faCirclePlus}
											/>
										</button>
									</div>
								);
							})
						)}
					</div>
				</div>

				<div className="em-selected-panel">
					<div className="em-selected-panel__head">
						<strong>Selected Groups</strong>
						{selectedIds.length > 0 && <small>Remove</small>}
					</div>

					{selectedIds.length === 0 ? (
						<div className="em-picker-empty">
							No groups added to this event yet.
						</div>
					) : (
						selectedIds.map((id) => {
							const g = detailsById.get(id) ?? { _id: id };
							return (
								<div className="em-group-row em-group-row--chosen" key={id}>
									<span className="em-group-row__icon">
										<img src={g.Icon || app.defaultGroupIcon} alt="" />
									</span>
									<span className="em-group-row__main">
										<strong>{groupLabel(g)}</strong>
										{g.MemberCount !== undefined && (
											<small>
												{g.MemberCount} Member
												{g.MemberCount === 1 ? "" : "s"}
											</small>
										)}
									</span>
									<button
										type="button"
										className="em-group-row__action em-group-row__action--danger"
										disabled={readOnly}
										title="Remove group"
										aria-label={`Remove ${groupLabel(g)}`}
										onClick={() => removeGroup(id)}
									>
										<FontAwesomeIcon icon={faTrashCan} />
									</button>
								</div>
							);
						})
					)}
				</div>
			</div>
		</div>
	);
};
